/*
MIT License

Copyright (c) 2018 react-hooks-org

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/
// Modified from original version to support multiple outside refs
import { useEffect, useRef, useCallback } from "react";
import { maybeToArray, maybeToElement } from "../utils";
/**
 *  useOutsideClick hook
 *
 * Checks if a click happened outside a Ref. Handy for dropdowns, modals and popups etc.
 *
 * @param ref Ref whose outside click needs to be listened to
 * @param handler Callback to fire on outside click
 * @param when A boolean which which activates the hook only when it is true. Useful for conditionally enable the outside click
 */
export function useOutsideClick(ref, handler, when) {
    if (when === void 0) { when = true; }
    var refs = useRef(maybeToArray(ref));
    refs.current = maybeToArray(ref);
    var savedHandler = useRef(handler);
    savedHandler.current = handler;
    var memoizedCallback = useCallback(function (e) {
        var isOutsideClick = refs.current.every(function (ref) {
            if (!ref) {
                // Ref isnt populated, so allow this ref to pass as satisfied
                return true;
            }
            var element = maybeToElement(ref);
            if (!element) {
                // Ref isnt populated, so allow this ref to pass as satisfied
                return true;
            }
            if (element.contains(e.target)) {
                // Clicked item was in this ref, so dont call the handler.
                return false;
            }
            return true;
        });
        if (isOutsideClick) {
            savedHandler.current(e);
        }
    }, []);
    useEffect(function () {
        // Note: This was changed from document to document.body
        // because we have mystery sourceless click events getting raised
        // on orphaned elements.
        // If the click handler needs to be placed on document again,
        // test TabbedMultiSelect to make sure its not collapsing when items
        // in the popper list are clicked.
        if (when) {
            document.body.addEventListener("mousedown", memoizedCallback);
            return function () {
                document.body.removeEventListener("mousedown", memoizedCallback);
            };
        }
    }, [when, memoizedCallback]);
}
//# sourceMappingURL=useOutsideClick.js.map